import { useEffect, useMemo, useRef, useState } from 'react';
import { type UseMutationResult } from '@tanstack/react-query';
import { trackEvent, AnalyticsEvent, pageSnapshotProperties } from '@/lib/analytics';
import { Button } from '@/components/ui/button';
import { AlertBanner } from '@/components/molecules/AlertBanner';
import { getErrorMessage } from '@/lib/errors';
import { toast } from '@/toast/useToast';
import { DateCarousel } from './DateCarousel';
import { TimeSlotGrid } from './TimeSlotGrid';
import { groupSlotsByDate, mergeSlotPages } from './slot-utils';
import type { AvailableSlot } from './schemas';

/** Structural rather than `./schemas` types: the partner booking mutation hits a different
 * endpoint with the same `{ startTime, timezone? } → { eventUri }` contract, and this keeps
 * an onboarding file from importing a settings-feature type. */
type BookingMutation = UseMutationResult<
  { eventUri: string },
  Error,
  { startTime: string; timezone?: string }
>;

/** Best-effort IANA timezone for the viewer, passed through to Calendly so
 * the invitee record — and its confirmation email — show local time instead
 * of UTC. `Intl` can throw in a locked-down sandbox. */
function getViewerTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

/** Shared footer for the loading/error/fallback branches below — the main
 * slot-picker branch has its own footer paired with the Confirm button. */
function ContinueLink({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <div className="flex items-center justify-between mt-4">
      <span />
      <button
        type="button"
        className="text-sm text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
        onClick={onClick}
      >
        {label}
      </button>
    </div>
  );
}

type Props = {
  calendlyUrl: string | undefined;
  slots: AvailableSlot[];
  /** Caller-owned so `CalendlyBookingDialog` can inject the onboarding or partner
   * (`useBookPartnerCalendlySlotMutation`) booking mutation per `calendlyTarget`. */
  bookingMutation: BookingMutation;
  /** Fetches one more page of slots from the given ISO-8601 cursor ("load more dates").
   * Caller-owned because onboarding and partner use different routes. Soft-fail:
   * `handleLoadMore` swallows rejections and the carousel stops offering more dates. */
  fetchMoreSlots: (cursorStartTime: string) => Promise<AvailableSlot[]>;
  /** Receives the booked slot's `startTime` (ISO-8601). */
  onBooked: (startTime: string) => void;
  /** Fires when the booking call itself fails (the inline error still shows). */
  onBookingError?: () => void;
  onSkip: () => void;
  loadingSlots: boolean;
  /** Error from the available-slots query, if it failed. */
  slotsError: unknown;
  /** Retry the available-slots query. */
  onRetrySlots: () => void;
  /**
   * Label for the "skip"/dismiss link shown alongside the loading, error,
   * and no-slots-fallback branches, and below the Confirm button on the
   * main picker. Defaults to the onboarding-flow copy — callers that reuse
   * this step outside onboarding (e.g. `CalendlyBookingDialog`, opened from
   * `NeedHelpConnectingBanner`) pass something that fits a dismissible
   * dialog instead of a forced first-run step.
   */
  skipLabel?: string;
  /**
   * `false` removes the skip link from the **main picker** — the branch where
   * a slot can actually be chosen — so booking is the only way forward. Used
   * by the partner access-request flow, where a call is mandatory.
   *
   * It deliberately does NOT reach the error and no-slots branches: those are
   * the states where booking is impossible, and removing the way out there
   * would trap the user rather than make them book. Callers that force the
   * step are expected to compute this from whether slots actually loaded —
   * see `CalendlyBookingDialog`'s `required` prop. The unconfigured-Calendly
   * auto-skip effect below is untouched for the same reason.
   */
  skippable?: boolean;
};

export function CalendlyStep({
  calendlyUrl,
  slots,
  bookingMutation,
  fetchMoreSlots,
  onBooked,
  onBookingError,
  onSkip,
  loadingSlots,
  slotsError,
  onRetrySlots,
  skipLabel = 'Continue to dashboard',
  skippable = true,
}: Props) {
  const loadingMoreRef = useRef(false);

  const [extraSlots, setExtraSlots] = useState<AvailableSlot[]>([]);
  const [lastPageEmpty, setLastPageEmpty] = useState(false);
  const [selectedDateKey, setSelectedDateKey] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [booked, setBooked] = useState(false);

  // If Calendly is fully unconfigured (no slots and no URL), skip the step.
  // We run this in an effect so the parent can swap state safely. Don't skip on
  // a query failure — `slots` is empty then too, but we want to surface a retry
  // instead of silently dismissing the step.
  useEffect(() => {
    if (!loadingSlots && !slotsError && slots.length === 0 && !calendlyUrl) {
      onSkip();
    }
  }, [loadingSlots, slotsError, slots.length, calendlyUrl, onSkip]);

  const allSlots = useMemo(() => mergeSlotPages(slots, extraSlots), [slots, extraSlots]);
  const grouped = useMemo(() => groupSlotsByDate(allSlots), [allSlots]);
  const activeDateKey = selectedDateKey ?? grouped[0]?.dateKey ?? null;
  const timeSlots = grouped.find((g) => g.dateKey === activeDateKey)?.slots ?? [];

  // Anchor for the 30-day pagination cap — the earliest date in the first
  // fetched page, independent of how many extra pages have since loaded.
  const firstFetchedDate = useMemo(() => {
    const times = slots
      .map((s) => new Date(s.startTime).getTime())
      .filter((t) => !Number.isNaN(t));
    return times.length > 0 ? new Date(Math.min(...times)) : new Date();
  }, [slots]);

  function handleSelectDate(dateKey: string) {
    // Ignore selection changes while a booking is in flight — otherwise the
    // UI can show a different date/slot "selected" than the one the pending
    // mutation is actually about to confirm, and onBooked() would fire for
    // whichever slot was captured at the original Confirm click regardless
    // of what's now shown as selected.
    if (bookingMutation.isPending) return;
    setSelectedDateKey(dateKey);
    setSelectedSlot(null);
  }

  function handleSelectSlot(slot: AvailableSlot) {
    if (bookingMutation.isPending || booked) return;
    setSelectedSlot(slot);
    setError(null);
  }

  async function handleLoadMore() {
    if (loadingMoreRef.current) return;
    const last = allSlots[allSlots.length - 1];
    if (!last) return;
    loadingMoreRef.current = true;
    try {
      const page = await fetchMoreSlots(last.startTime);
      setExtraSlots((prev) => mergeSlotPages(prev, page));
      // ponytail: literally-empty-page check, not "no new slots" — at the
      // true tail the window can re-return only the (deduped) cursor slot,
      // so this can miss it and fall back on the 30-day cap. Upgrade to a
      // known-startTime diff if that proves user-visible.
      setLastPageEmpty(page.length === 0);
    } catch {
      // ponytail: soft-fail, carousel just stops offering more dates. Not
      // worth an inline error banner for this affordance.
    } finally {
      loadingMoreRef.current = false;
    }
  }

  async function handleConfirm() {
    if (!selectedSlot || bookingMutation.isPending || booked) return;
    setError(null);
    try {
      await bookingMutation.mutateAsync({
        startTime: selectedSlot.startTime,
        timezone: getViewerTimezone(),
      });
      setBooked(true);
      trackEvent(AnalyticsEvent.DemoMeetingBooked, {
        selected_slot: selectedSlot.startTime,
        ...pageSnapshotProperties(),
      });
      toast.success("You're booked! Calendly just emailed you the meeting details.");
      onBooked(selectedSlot.startTime);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not book this slot. Please try another time.'));
      onBookingError?.();
    }
  }

  if (loadingSlots) {
    return (
      <div className="flex flex-col gap-2" aria-busy="true">
        <p className="text-sm text-muted-foreground">Finding available times…</p>
      </div>
    );
  }

  if (slotsError) {
    return (
      <div className="flex flex-col gap-2">
        <AlertBanner className="mb-4">
          {getErrorMessage(slotsError, "We couldn't load available times.")}
        </AlertBanner>
        <Button onClick={onRetrySlots} size="lg" className="w-full">
          Try again
        </Button>
        <ContinueLink onClick={onSkip} label={skipLabel} />
      </div>
    );
  }

  if (slots.length === 0 && calendlyUrl) {
    return (
      <div className="flex flex-col gap-2">
        <p className="text-sm text-muted-foreground mb-4">
          We&apos;d love to give you a personalised walkthrough. Pick a time on
          our calendar, or skip and we&apos;ll catch up later.
        </p>
        <Button asChild size="lg" className="w-full">
          <a href={calendlyUrl} target="_blank" rel="noopener noreferrer">
            Schedule on Calendly →
          </a>
        </Button>
        <ContinueLink onClick={onSkip} label={skipLabel} />
      </div>
    );
  }

  if (slots.length === 0) {
    return null;
  }

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="text-center">
        <div className="text-3xl mb-1" aria-hidden="true">
          📅
        </div>
        <p className="text-sm text-muted-foreground">
          Pick a 30-minute slot for a personalised onboarding session.
        </p>
      </div>

      {error && <AlertBanner>{error}</AlertBanner>}

      <DateCarousel
        groups={grouped}
        selectedDateKey={activeDateKey}
        onSelectDate={handleSelectDate}
        firstFetchedDate={firstFetchedDate}
        lastPageWasEmpty={lastPageEmpty}
        onLoadMore={() => void handleLoadMore()}
      />

      <TimeSlotGrid slots={timeSlots} selectedSlot={selectedSlot} onSelect={handleSelectSlot} />

      <div className="flex flex-col gap-3 mt-2">
        <Button
          size="lg"
          className="w-full"
          disabled={!selectedSlot || bookingMutation.isPending || booked}
          onClick={() => void handleConfirm()}
        >
          {selectedSlot ? 'Confirm' : 'Select a time slot'}
        </Button>
        {skippable && (
          <div className="flex items-center justify-center">
            <button
              type="button"
              className="text-sm text-muted-foreground hover:text-foreground underline-offset-2 hover:underline disabled:opacity-50"
              onClick={onSkip}
              disabled={bookingMutation.isPending}
            >
              {skipLabel}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
